import { useState, useEffect, useCallback } from 'react';
import { auditLogsApi, AuditLogsQueryParams } from '../api/auditLogsApi';
import { AuditLogEntry, AuditLogFilters } from '../types';

interface UseAuditLogsResult {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  pages: number;
  isLoading: boolean;
  error: string | null;
  filters: AuditLogFilters | null;
  setPage: (page: number) => void;
  setEntityType: (type: string) => void;
  setAction: (action: string) => void;
  setSearch: (search: string) => void;
  setDateRange: (start: string, end: string) => void;
  refetch: () => void;
  entityType: string;
  action: string;
  search: string;
  startDate: string;
  endDate: string;
}

export function useAuditLogs(
  defaultEntityType?: string,
  defaultAction?: string,
): UseAuditLogsResult {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters | null>(null);

  const [entityType, setEntityType] = useState(defaultEntityType || '');
  const [action, setAction] = useState(defaultAction || '');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch filter options once
  useEffect(() => {
    auditLogsApi.getFilters().then(setFilters).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: AuditLogsQueryParams = { page, per_page: 15 };
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await auditLogsApi.getLogs(params);
      setLogs(res.logs);
      setTotal(res.total);
      setPages(res.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, entityType, action, search, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSetEntityType = useCallback((type: string) => {
    setEntityType(type);
    setPage(1);
  }, []);

  const handleSetAction = useCallback((a: string) => {
    setAction(a);
    setPage(1);
  }, []);

  const handleSetSearch = useCallback((s: string) => {
    setSearch(s);
    setPage(1);
  }, []);

  const handleSetDateRange = useCallback((start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    setPage(1);
  }, []);

  return {
    logs, total, page, pages, isLoading, error, filters,
    setPage, setEntityType: handleSetEntityType, setAction: handleSetAction,
    setSearch: handleSetSearch, setDateRange: handleSetDateRange, refetch: fetchLogs,
    entityType, action, search, startDate, endDate,
  };
}
